/**
 * renderMemberRoutes returns the full CLIENT route tree (member layout + nested
 * pages). It maintains the previous structure while allowing AppContent to drop
 * the JSX directly within `<Routes>`.
 */
import { Route, Navigate } from 'react-router-dom';
import { MEMBER_ROUTE_PERMISSION } from '@/config/navigation-permission-registry';
import HipaaConsentCheck from '../../components/auth/HipaaConsentCheck';
import PermissionBasedRoute from '../../components/auth/PermissionBasedRoute';
import FallbackSpinner from '../../components/FallbackSpinner';
import { CartProvider } from '../../contexts/CartContext';
import { Client } from '../../types/clientTypes';
import { InClinicRedirect } from './InClinicRedirect';
import {
  MemberLayout,
  IdeasPage,
  MemberHome,
  MemberProfile,
  MemberThriveOnboard,
  MemberBookTest,
  MemberThriveCheck,
  MemberCognitiveAssessment,
  MemberIntakeCall,
  MemberIntakeForm,
  MemberInCenterVisit,
  MemberLongevityConsultation,
  MemberInCenterSteps,
  MemberReports,
  MemberClinicalNotes,
  MemberSupport,
  MemberCheckout,
  MemberTodo,
  MemberProduct,
  MemberMarketplace,
  MemberMyCarts,
  MemberMyPurchases,
  MemberMemberships,
  MemberMyBenefits,
  ThyrocareRedemption,
  CenterCheckinRedemption,
  MemberSettings,
  MemberHealthData,
  MemberHealthSnapshot,
  MemberPathwayDetails,
  MemberBiomarkerDetails,
  MemberNotifications,
  MemberUserVisits,
  MemberThriveKommune,
  MemberLabTest,
  MemberBoxBreathe,
  MemberFamily,
  MemberVideos,
  // GiftThriveEarly, // Gift Membership hidden — disabled for now
  WhatsNewPage,
  WearableConnectionsPage,
  ConnectDevicePage,
  OAuthCallbackPage,
  SleepListPage,
  SleepDetailPage,
  ActivityListPage,
  ActivityDetailPage,
  DailyListPage,
  DailyDetailPage,
  BodyListPage,
  DailyCheckinPage,
  DailyCheckinHistoryPage,
  DailyCheckinDashboardPage,
  DailyCheckinSettingsPage,
  ChatFullPage,
} from './lazyPages';

type MemberRoutesProps = {
  selectedClient: Client;
  isMobileSidebarOpen: boolean;
  onMobileSidebarToggle: () => void;
  sidebarMode: import('@/config/sidebar-config').SidebarMode;
  onSidebarToggle: () => void;
  // Saved homepage for the user (already expanded with the active client id).
  // When set and valid, used in place of the default /member/home/:clientId redirect.
  effectiveHomePath?: string | null;
  // Whether the web-app chat feature flag (enable_in_app_chat_webapp) is on.
  inAppChatWebappEnabled: boolean;
  areFeatureFlagsLoading: boolean;
};

export const renderMemberRoutes = ({
  selectedClient,
  isMobileSidebarOpen,
  onMobileSidebarToggle,
  sidebarMode,
  onSidebarToggle,
  effectiveHomePath,
  inAppChatWebappEnabled,
  areFeatureFlagsLoading,
}: MemberRoutesProps) => {
  const memberHome = effectiveHomePath ?? `/member/home/${selectedClient?.id}`;
  return (
    <Route element={<PermissionBasedRoute requiredPermission={MEMBER_ROUTE_PERMISSION} />}>
      <Route
        element={
          <CartProvider>
            <HipaaConsentCheck selectedClient={selectedClient}>
              <MemberLayout
                selectedClient={selectedClient}
                isMobileSidebarOpen={isMobileSidebarOpen}
                onMobileSidebarToggle={onMobileSidebarToggle}
                sidebarMode={sidebarMode}
                onSidebarToggle={onSidebarToggle}
              />
            </HipaaConsentCheck>
          </CartProvider>
        }
      >
        <Route path="/" element={<Navigate to={memberHome} replace />} />
        <Route path="/member" element={<Navigate to={memberHome} replace />} />
        <Route
          path="/member/home/:clientId"
          element={
            <MemberHome
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route path="/ideas" element={<IdeasPage />} />
        <Route
          path="/member/profile/:clientId"
          element={
            <MemberProfile
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/thrive-onboard/:clientId"
          element={<MemberThriveOnboard selectedClient={selectedClient} />}
        />
        <Route
          path="/member/book-test/:clientId"
          element={<MemberBookTest selectedClient={selectedClient} />}
        />
        <Route
          path="/member/thrive-check/:clientId"
          element={<MemberThriveCheck selectedClient={selectedClient} />}
        />
        <Route
          path="/member/cognitive-assessment/:clientId"
          element={<MemberCognitiveAssessment selectedClient={selectedClient} />}
        />
        <Route
          path="/member/intake-call/:clientId"
          element={<MemberIntakeCall selectedClient={selectedClient} />}
        />
        <Route
          path="/member/intake-form/:clientId"
          element={<MemberIntakeForm selectedClient={selectedClient} />}
        />
        {/* Legacy in-clinic booking (static slots, no Google Calendar):
            the synced In Centre Visit flow replaces it. */}
        <Route path="/member/in-clinic/:clientId" element={<InClinicRedirect />} />
        <Route
          path="/member/in-center-visit/:clientId"
          element={<MemberInCenterVisit selectedClient={selectedClient} />}
        />
        <Route
          path="/member/longevity-consultation/:clientId"
          element={<MemberLongevityConsultation selectedClient={selectedClient} />}
        />
        <Route
          path="/member/in-center-steps/:clientId"
          element={<MemberInCenterSteps selectedClient={selectedClient} />}
        />
        <Route
          path="/member/support/:clientId"
          element={
            <MemberSupport
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/todo/:clientId"
          element={
            <MemberTodo
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/product/:clientId"
          element={<MemberProduct selectedClient={selectedClient} />}
        />
        <Route
          path="/member/marketplace/:clientId"
          element={
            <MemberMarketplace
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/my-carts/:clientId"
          element={
            <MemberMyCarts
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/my-purchases/:clientId"
          element={
            <MemberMyPurchases
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/memberships/:clientId"
          element={
            <MemberMemberships
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/benefits/:clientId"
          element={
            <MemberMyBenefits
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/redeem/thyrocare/:clientId"
          element={<ThyrocareRedemption selectedClient={selectedClient} />}
        />
        <Route
          path="/member/redeem/center-checkin/:clientId"
          element={<CenterCheckinRedemption />}
        />
        <Route
          path="/member/settings/:clientId"
          element={
            <MemberSettings
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/user-visits/:clientId"
          element={<MemberUserVisits selectedClient={selectedClient} />}
        />
        <Route
          path="/member/my-data/:clientId"
          element={
            <MemberHealthData
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/health-snapshot/:clientId"
          element={
            <MemberHealthSnapshot
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/reports/:clientId"
          element={
            <MemberReports
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/clinical-notes/:clientId"
          element={
            // ``key`` forces a remount when the staff-side acting-on-behalf
            // client switches, so previous-patient notes never flash through.
            <MemberClinicalNotes
              key={selectedClient?.user_profile_id ?? 'no-client'}
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/pathway-details/:clientId/:pathwayName"
          element={<MemberPathwayDetails selectedClient={selectedClient} />}
        />
        <Route
          path="/member/biomarker-details/:clientId/:biomarkerName"
          element={<MemberBiomarkerDetails selectedClient={selectedClient} />}
        />
        <Route
          path="/member/thrive-kommune/:clientId"
          element={
            <MemberThriveKommune
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/notifications/:clientId"
          element={<MemberNotifications selectedClient={selectedClient} />}
        />
        <Route
          path="/chat"
          element={
            areFeatureFlagsLoading ? (
              <FallbackSpinner />
            ) : inAppChatWebappEnabled ? (
              <ChatFullPage />
            ) : (
              <Navigate to={memberHome} replace />
            )
          }
        />
        <Route
          path="/member/chat"
          element={
            areFeatureFlagsLoading ? (
              <FallbackSpinner />
            ) : inAppChatWebappEnabled ? (
              <Navigate to="/chat" replace />
            ) : (
              <Navigate to={memberHome} replace />
            )
          }
        />
        <Route path="/member/checkout" element={<MemberCheckout />} />
        <Route
          path="/member/lab-test/:clientId"
          element={<MemberLabTest selectedClient={selectedClient} />}
        />
        <Route
          path="/member/box-breathe/:clientId"
          element={<MemberBoxBreathe selectedClient={selectedClient} />}
        />
        {/* Gift Membership route hidden — disabled for now
      <Route
        path="/member/gift-thrive-early/:clientId"
        element={<GiftThriveEarly selectedClient={selectedClient} />}
      />
      */}
        <Route path="/member/whats-new/:clientId" element={<WhatsNewPage />} />
        <Route
          path="/member/knowledge-base/:clientId"
          element={<Navigate to={memberHome} replace />}
        />
        <Route
          path="/member/family/:clientId"
          element={
            <MemberFamily
              selectedClient={selectedClient}
              onMobileSidebarToggle={onMobileSidebarToggle}
            />
          }
        />
        <Route
          path="/member/videos/:clientId"
          element={<MemberVideos selectedClient={selectedClient} />}
        />
        {/* Daily Check-in Routes */}
        <Route path="/member/daily-checkin/:clientId" element={<DailyCheckinPage />} />
        <Route
          path="/member/daily-checkin-history/:clientId"
          element={<DailyCheckinHistoryPage />}
        />
        <Route
          path="/member/daily-checkin-dashboard/:clientId"
          element={<DailyCheckinDashboardPage />}
        />
        <Route
          path="/member/daily-checkin-settings/:clientId"
          element={<DailyCheckinSettingsPage />}
        />
        {/* Wearables Routes */}
        <Route path="/member/wearables/callback/:provider" element={<OAuthCallbackPage />} />
        <Route
          path="/member/wearables/:clientId"
          element={<WearableConnectionsPage onMobileSidebarToggle={onMobileSidebarToggle} />}
        />
        <Route path="/member/wearables/:clientId/connect" element={<ConnectDevicePage />} />
        <Route path="/member/wearables/:clientId/sleep" element={<SleepListPage />} />
        <Route path="/member/wearables/:clientId/sleep/:id" element={<SleepDetailPage />} />
        <Route path="/member/wearables/:clientId/activity" element={<ActivityListPage />} />
        <Route path="/member/wearables/:clientId/activity/:id" element={<ActivityDetailPage />} />
        <Route path="/member/wearables/:clientId/daily" element={<DailyListPage />} />
        <Route path="/member/wearables/:clientId/daily/:id" element={<DailyDetailPage />} />
        <Route path="/member/wearables/:clientId/body" element={<BodyListPage />} />
      </Route>
    </Route>
  );
};
